import generateElementId from '../utils/generateElementId';
import getQueryParam from '../utils/getQueryParam';
import kebabCase from '../utils/kebabCase';
import { encryptOnlineId } from '../utils/onlineIdInUrl';
import { PS_VISA_PATH, PS_VISA_STORE_NUMBER_PDC, PS_VISA_STORE_NUMBER_WEB } from '../constants/constants';
import { storeToPdcMapping } from '../utils/l10n';

const anchorContent = (anchorData) => {
  const { iconId } = anchorData;

  return iconId ? `<span class="shared-nav-icon-text">${anchorData.label}</span>` : anchorData.label;
};

const anchorClass = (anchorData, type) => {
  const { mobileNavIcon, iconId } = anchorData;
  let classes = `shared-nav__${type}-anchor shared-nav-anchor dtm-no-track `;

  if (mobileNavIcon) {
    classes += `shared-nav-icon shared-nav-mobile-icon shared-nav-icon--${mobileNavIcon} `;
  }

  if (iconId) {
    classes += `shared-nav-link-icon shared-nav-link-icon--${iconId}`;
  }

  if (type === 'link-list') {
    classes += 'shared-nav-icon shared-nav-icon--tertiary-marker';
  }

  return classes;
};

const createSMCID = ({ hostname, pathname }, { anchorData, locale, section }) => {
  const filteredPathname = encryptOnlineId(pathname);
  const pagename = (filteredPathname !== '/') ? filteredPathname.split('/').filter(entry => entry !== '') : [];

  let formattedPagename = '';
  let hostapp = 'other';

  if (hostname === 'my.playstation.com') {
    hostapp = 'social';
  } else if (hostname === 'store.playstation.com') {
    hostapp = 'store';
  } else if (hostname.indexOf('.playstation.com') !== -1) {
    hostapp = 'pdc';
  }

  if (pagename[0] && pagename[0].toLowerCase() === locale) {
    pagename.shift();
  }

  if (pagename.length) {
    formattedPagename = `:${pagename.join('-')}`;
  }

  return `${hostapp}:${locale}${formattedPagename}:primary nav:${kebabCase(section)}:${kebabCase(anchorData.label)}`;
};

const replaceLocaleInPath = (url, locale) => {
  const localeRegex = /\/[A-Za-z]{2,4}([_-]([A-Za-z]{4}|[0-9]{3}))?([_-]([A-Za-z]{2}|[0-9]{3}))\/?/;
  const match = url.match(localeRegex);
  let mappedUrl = url;

  if (match) {
    mappedUrl = mappedUrl.replace(localeRegex, `/${locale}/`);
  }

  return mappedUrl;
};

// adds storeNumber for PlayStation Visa URLs only
export const getStoreNumber = (anchorLink) => {
  let storeNumber;
  if (anchorLink.indexOf(PS_VISA_PATH) !== -1) {
    if (anchorLink.indexOf('store.playstation.com') !== -1 || (anchorLink.indexOf('webstore.e1-np.') !== -1) || (anchorLink.indexOf('webstore.mgmt') !== -1)) {
      storeNumber = PS_VISA_STORE_NUMBER_WEB;
    } else {
      storeNumber = PS_VISA_STORE_NUMBER_PDC;
    }
  }
  return storeNumber;
};

export const getAnchorLink = (location, locale, baseUrl, anchorData, section) => {
  const { link, stringId } = anchorData;
  const smcid = createSMCID(location, {
    anchorData,
    locale,
    section,
  });
  const emcid = getQueryParam(location.search, 'emcid');
  const queryParams = [];
  let anchorLink = (link.indexOf('http') !== 0) ? baseUrl + link : link;
  // Rewrite store and help links
  // if locale is a key in store to pdc mapping, do this
  Object.keys(storeToPdcMapping).forEach((storeLocale) => {
    if (locale === storeLocale) {
      if (anchorLink.indexOf('store.playstation.com') !== -1
        || anchorLink.indexOf('status.playstation.com') !== -1) {
        anchorLink = replaceLocaleInPath(anchorLink, locale);
      }
    }
  });

  if (smcid) {
    queryParams.push(`smcid=${encodeURIComponent(smcid)}`);
  }

  const storeNumber = getStoreNumber(anchorLink);

  if (storeNumber) {
    queryParams.push(`storeNumber=${encodeURIComponent(storeNumber)}`);
  }

  if (emcid) {
    queryParams.push(`emcid=${encodeURIComponent(emcid)}`);
  }

  if (queryParams.length && stringId !== 'msg_hardware_and_discs') {
    anchorLink += (anchorLink.indexOf('?') !== -1) ? '&' : '?';
    anchorLink += queryParams.join('&');
  }
  return anchorLink;
};

export default (
  { location = {} },
  {
    locale,
    baseUrl,
    anchorData,
    section,
    type,
    indexedId,
  },
) => {
  const { stringId, newWindow, mobileNavIcon } = anchorData;
  const l2StringId = indexedId || `${section}_${stringId}`;
  const id = generateElementId(`link-${type}`, l2StringId);
  let labelData = `${anchorContent(anchorData)}`;
  const telemetryDataAttribute = mobileNavIcon
    ? `data-telemetry='${JSON.stringify({ topLevelHeaderName: mobileNavIcon })}'`
    : '';

  const anchorLink = getAnchorLink(location, locale, baseUrl, anchorData, section);

  if (type === 'link-list') {
    labelData = `<span>${anchorContent(anchorData)}</span>`;
  }

  return `
    <a id="${id}" class="${anchorClass(anchorData, type)}" href="${anchorLink}" ${telemetryDataAttribute} ${(newWindow) ? 'target="_blank"' : ''}>
      ${labelData}
    </a>
  `;
};
